import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Users, Gauge, RotateCcw, ArrowRight, CheckCircle2, AlertCircle, Trophy, Repeat, Plus, Library, Layers } from "lucide-react";
import { Header } from "@/components/neusim/Header";
import { AdminNav } from "@/components/neusim/AdminNav";
import { LeaderboardModal } from "@/components/neusim/LeaderboardModal";
import { DownloadCoachingPdfButton } from "@/components/neusim/DownloadCoachingPdfButton";
import { useSimulations } from "@/hooks/useSimulations";
import type { SimulationSummary } from "@/types/simulation";

function difficultyColor(difficulty: string | null): string {
  switch ((difficulty ?? "").toLowerCase()) {
    case "high":
      return "border-neusim-band-low/40 bg-neusim-band-low/10 text-[#f87272]";
    case "medium":
      return "border-neusim-band-medium/40 bg-neusim-band-medium/10 text-[#f59e0b]";
    case "low":
      return "border-neusim-band-high/40 bg-neusim-band-high/10 text-[#10b981]";
    default:
      return "border-white/15 bg-white/5 text-white/60";
  }
}

function SimulationCard({ sim, onStart, onOpenLeaderboard, onOpenReplay }: { sim: SimulationSummary; onStart: () => void; onOpenLeaderboard: () => void; onOpenReplay: () => void }): JSX.Element {
  const canResume = sim.hasInProgress;
  const exhausted = !canResume && sim.attemptsCompleted >= sim.maxAttempts;
  const startedBefore = !canResume && sim.attemptsCompleted > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-6 backdrop-blur-xl"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />

      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-bold leading-tight text-white">{sim.title}</h2>
        {sim.difficulty !== null && (
          <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${difficultyColor(sim.difficulty)}`}>
            {sim.difficulty}
          </span>
        )}
      </div>

      {sim.description !== null && (
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-white/65">{sim.description}</p>
      )}

      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-white/60">
        {sim.competencyName !== null && (
          <span className="inline-flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5 text-fuchsia-300" />
            {sim.competencyName}
          </span>
        )}
        {sim.audience !== null && (
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-cyan-300" />
            {sim.audience}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <Gauge className="h-3.5 w-3.5 text-violet-300" />
          {sim.attemptsCompleted} / {sim.maxAttempts} attempts
        </span>
      </div>

      {/* Action bar — single flex row. Secondary actions become icon-only
          squares (~32px) so 1–3 of them fit comfortably alongside Start at
          almost any card width; ml-auto pushes Start to the right edge.
          The "All attempts used" exhausted state still shows inline. The old
          "Start a new attempt" label was dropped — it duplicated the "X / Y
          attempts" gauge above and just added vertical noise. */}
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-5">
        {exhausted && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-neusim-band-high">
            <CheckCircle2 className="h-4 w-4" /> All attempts used
          </span>
        )}
        {sim.attemptsReplayable >= 2 && (
          <button
            type="button"
            onClick={onOpenReplay}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 transition-colors hover:border-cyan-400/60 hover:bg-cyan-500/15"
            title="Replay — compare your past attempts"
            aria-label="Replay"
          >
            <Repeat className="h-3.5 w-3.5" />
          </button>
        )}
        {sim.leaderboardEnabled && (
          <button
            type="button"
            onClick={onOpenLeaderboard}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-amber-400/30 bg-amber-500/10 text-amber-200 transition-colors hover:border-amber-400/60 hover:bg-amber-500/15"
            title="Leaderboard"
            aria-label="Leaderboard"
          >
            <Trophy className="h-3.5 w-3.5" />
          </button>
        )}
        {sim.latestCompletedAttemptId !== null && (
          <DownloadCoachingPdfButton
            attemptId={sim.latestCompletedAttemptId}
            simulationTitle={sim.title}
            compact
            iconOnly
          />
        )}
        <button
          type="button"
          onClick={onStart}
          disabled={exhausted}
          className="ml-auto inline-flex items-center gap-1.5 whitespace-nowrap rounded-xl bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-500 px-5 py-2.5 text-xs font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition-transform hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
        >
          {canResume ? <RotateCcw className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />}
          {canResume ? "Resume" : startedBefore ? "Start again" : "Start"}
        </button>
      </div>
    </motion.div>
  );
}

function SkeletonCard(): JSX.Element {
  return (
    <div className="h-56 animate-pulse rounded-2xl border border-white/10 bg-white/[0.04]" />
  );
}

export function SimulationSelectPage(): JSX.Element {
  const { loading, error, simulations, role, reload } = useSimulations();
  const navigate = useNavigate();
  const [leaderboardFor, setLeaderboardFor] = useState<SimulationSummary | null>(null);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-neusim-bg font-sans text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 -top-40 h-[700px] w-[700px] rounded-full"
        style={{ background: "#a855f7", opacity: 0.18, filter: "blur(180px)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-40 h-[700px] w-[700px] rounded-full"
        style={{ background: "#ec4899", opacity: 0.15, filter: "blur(180px)" }}
      />

      <Header />

      <main className="relative mx-auto max-w-[1200px] px-6 pb-16 pt-24">
        {(role === "super_admin" || role === "client_admin") && (
          <div className="mb-6"><AdminNav showHome={false} /></div>
        )}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-fuchsia-300">
              Your simulations
            </span>
            <h1 className="mt-1 text-3xl font-bold text-white">Choose a simulation to begin</h1>
            <p className="mt-2 text-sm text-white/60">
              Each simulation is a series of leadership decisions. Your responses are scored and you receive
              personalised coaching at the end.
            </p>
          </div>
          {/* AdminNav lives above the page heading (see below) so admins get the
              same nav chips here as on every admin page. */}
        </div>

        {loading && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {!loading && error !== null && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-neusim-band-low/30 bg-neusim-band-low/5 px-6 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-[#f87272]" />
            <h2 className="mt-4 text-lg font-semibold">Couldn&rsquo;t load your simulations</h2>
            <p className="mt-1 max-w-md text-sm text-white/60">{error}</p>
            <button
              type="button"
              onClick={reload}
              className="mt-6 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10"
            >
              <RotateCcw className="h-4 w-4" /> Try again
            </button>
          </div>
        )}

        {!loading && error === null && simulations.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-16 text-center">
            <Target className="h-10 w-10 text-white/30" />
            <h2 className="mt-4 text-lg font-semibold">No simulations assigned yet</h2>
            <p className="mt-1 max-w-md text-sm text-white/60">
              Once your facilitator assigns you a simulation, it will appear here.
            </p>
          </div>
        )}

        {!loading && error === null && simulations.length > 0 && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {simulations.map((sim) => (
              <SimulationCard
                key={sim.id}
                sim={sim}
                onStart={() => navigate(`/sim/${sim.id}/intro`)}
                onOpenLeaderboard={() => setLeaderboardFor(sim)}
                onOpenReplay={() => navigate(`/sim/${sim.id}/replay`)}
              />
            ))}
          </div>
        )}
      </main>

      {leaderboardFor !== null && (
        <LeaderboardModal
          simId={leaderboardFor.id}
          simTitle={leaderboardFor.title}
          onClose={() => setLeaderboardFor(null)}
        />
      )}
    </div>
  );
}
